import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { Bell, Check, X, AtSign } from "lucide-react";
import { Link } from "react-router-dom";

const isMention = (n) => (n.kind || "").includes("mention");

const NotificationBell = ({ user }) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("all");
  const [data, setData] = useState({ items: [], unread: 0 });

  const load = () => {
    if (!user) return;
    api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    if (!user) return;
    const t = setInterval(load, 25000);
    return () => clearInterval(t);
  }, [user?.id]); // eslint-disable-line

  if (!user) return null;
  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    load();
  };
  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  const mentionUnread = data.items.filter((n) => isMention(n) && !n.read).length;
  const visible = tab === "mentions" ? data.items.filter(isMention) : data.items;

  return (
    <div className="relative">
      <button
        data-testid="notif-bell-btn"
        onClick={() => setOpen((o) => !o)}
        className="relative w-9 h-9 flex items-center justify-center bg-[#13151B] border border-[#2A2E3D] hover:border-[#D4A94A] tactical-frame-sm text-[#94A3B8] hover:text-[#D4A94A] transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-4 h-4" />
        {data.unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#D4A94A] text-black text-[9px] font-bold flex items-center justify-center">
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[45]" onClick={() => setOpen(false)} />
          <div data-testid="notif-panel" className="!absolute right-0 top-full mt-2 w-80 max-w-[calc(100vw-1rem)] bg-[#13151B] border border-[#2A2E3D] tactical-frame z-50 max-h-[70vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-3 border-b border-[#2A2E3D]">
              <div className="overline">// COMMS LOG</div>
              <div className="flex items-center gap-2">
                <button data-testid="notif-mark-all" onClick={markAll} className="text-xs font-mono text-[#D4A94A] hover:text-[#E4BC5D]">Mark all read</button>
                <button onClick={() => setOpen(false)}><X className="w-4 h-4 text-[#94A3B8]" /></button>
              </div>
            </div>
            <div className="flex items-center gap-1 px-3 pt-2.5 border-b border-[#2A2E3D]">
              <button data-testid="notif-tab-all" onClick={() => setTab("all")} className={`font-stencil text-[11px] uppercase px-3 py-1.5 border-b-2 -mb-px transition-colors ${tab === "all" ? "border-[#D4A94A] text-[#D4A94A]" : "border-transparent text-[#64748B] hover:text-[#94A3B8]"}`}>All</button>
              <button data-testid="notif-tab-mentions" onClick={() => setTab("mentions")} className={`inline-flex items-center gap-1 font-stencil text-[11px] uppercase px-3 py-1.5 border-b-2 -mb-px transition-colors ${tab === "mentions" ? "border-[#D4A94A] text-[#D4A94A]" : "border-transparent text-[#64748B] hover:text-[#94A3B8]"}`}><AtSign className="w-3 h-3" /> Mentions{mentionUnread > 0 && <span className="min-w-[15px] h-[15px] px-1 rounded-full bg-[#D4A94A] text-black text-[9px] font-bold flex items-center justify-center">{mentionUnread > 9 ? "9+" : mentionUnread}</span>}</button>
            </div>
            <div className="flex-1 overflow-auto">
              {visible.length === 0 && <div className="p-6 text-center text-sm font-mono text-[#64748B]">{tab === "mentions" ? "// NO MENTIONS" : "// NO TRANSMISSIONS"}</div>}
              {visible.map((n) => (
                <div key={n.id} data-testid={`notif-item-${n.id}`} className={`p-3 border-b border-[#2A2E3D] hover:bg-[#0B0C0E] ${!n.read ? "border-l-2 border-l-[#D4A94A]" : ""}`}>
                  <div className="flex items-start gap-2">
                    {isMention(n) && <AtSign data-testid={`notif-mention-icon-${n.id}`} className="w-4 h-4 text-[#D4A94A] mt-0.5 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      {n.link ? (
                        <Link to={n.link} onClick={() => { markRead(n.id); setOpen(false); }} className="block">
                          <div className="font-stencil text-xs font-bold uppercase text-[#F1F5F9] truncate">{n.title}</div>
                          {n.body && <div className="text-xs text-[#94A3B8] mt-0.5 line-clamp-2">{n.body}</div>}
                        </Link>
                      ) : (
                        <>
                          <div className="font-stencil text-xs font-bold uppercase truncate">{n.title}</div>
                          {n.body && <div className="text-xs text-[#94A3B8] mt-0.5 line-clamp-2">{n.body}</div>}
                        </>
                      )}
                      <div className="font-mono text-[10px] text-[#64748B] mt-1">{new Date(n.created_at).toLocaleString()}</div>
                    </div>
                    {!n.read && <button onClick={() => markRead(n.id)} className="text-[#D4A94A] hover:text-[#E4BC5D]"><Check className="w-4 h-4" /></button>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
